/**
 * One station tile: the surface it is built into, the fixture standing on it,
 * whatever is sitting there, and its bar.
 *
 * No words in this file. It used to print `stove · pan` and `fire ext.` in
 * 7-point type on a coloured square; the reference screenshot
 * has no text anywhere inside the kitchen and now neither do we. What a player
 * reads instantly is *state* — is that plate clean, is that pan about to burn,
 * is the board halfway through a chop — and a drawn burner with a pan on it
 * says that at 32 pixels where a label does not.
 *
 * Every station except the floor and the wall is built into one continuous
 * walnut bench, which is what makes a kitchen read as a kitchen rather than as
 * a scatter of coloured squares. Every decision about *time* still comes from
 * `progress.ts`, which is unit tested; this file is geometry.
 */

import type { Carryable, Station, StationType } from '@overcooked/shared'
import type { Catalog } from './catalog'
import {
  drawCarryable,
  drawExtinguisher,
  drawExtinguisherSpray,
  drawFlame,
  drawIngredient,
  drawStationFixture,
  RACKED_EXTINGUISHER,
} from './icons'
import type { Bar } from './progress'
import {
  barMetrics,
  extinguishProgress,
  holdAffordance,
  holdDisplay,
  isSpraying,
  itemLabel,
  panDisplay,
} from './progress'
import { COLOURS, font, roundRect, shade } from './theme'

const TAU = Math.PI * 2

interface TileBox {
  x: number
  y: number
  size: number
}

/**
 * Where the things a tile holds sit on it, as fractions of the tile: centre
 * `x`, `y` and icon half-width `r`.
 *
 * A pan sits a little left of centre because its handle runs out to the
 * right, and on a stove it sits up on the burner ring rather than in the
 * middle of the hob, so the ring glows round it.
 */
export const TILE_PLACEMENT = {
  crateIngredient: { x: 0.5, y: 0.52, r: 0.24 },
  panOnStove: { x: 0.46, y: 0.38, r: 0.3 },
  pan: { x: 0.46, y: 0.5, r: 0.3 },
  rackedExtinguisher: RACKED_EXTINGUISHER,
  item: { x: 0.5, y: 0.44, r: 0.34 },
} as const

type Placement = { x: number; y: number; r: number }

/** Where `item` sits on a station of this type. */
export function itemPlacement(type: StationType, item: Carryable): Placement {
  if (item.type === 'container') return TILE_PLACEMENT.pan
  if (item.type === 'equipment' && type === 'extinguisher_rack') return TILE_PLACEMENT.rackedExtinguisher
  return TILE_PLACEMENT.item
}

/** Where the container standing on a piece of cooking equipment sits. */
export const containerPlacement = (type: StationType): Placement =>
  type === 'stove' ? TILE_PLACEMENT.panOnStove : TILE_PLACEMENT.pan

/** Stations that are cut into the bench rather than standing on it. */
const RECESSED: ReadonlySet<StationType> = new Set<StationType>(['sink', 'bin', 'hatch', 'plate_return'])

/**
 * The one "draw a station tile" function.
 *
 * The round, the replay, the editor's preview and the editor's palette swatches
 * all paint a tile by calling this, so a brush in the palette looks exactly like
 * what it paints and a fixture redrawn here is redrawn everywhere at once.
 */
export function drawStation(
  ctx: CanvasRenderingContext2D,
  station: Station,
  box: TileBox,
  atTick: number,
  catalog: Catalog,
  /**
   * The angle the extinguisher's spray travels, in radians, when somebody is
   * holding the key at this fire. `null` means nobody is — which is every tile
   * on almost every frame. The caller works it out because only it knows where
   * the chef holding the key is standing; a station knows only that it is held.
   */
  sprayAngle: number | null = null,
  /**
   * The seat colour of whoever is holding the key at this station, or `null`
   * when nobody is. Resolved by the caller for the same reason `sprayAngle` is:
   * a station knows it is held, only the state knows by whom.
   */
  holderColour: string | null = null,
): void {
  drawSurface(ctx, station, box)

  if (station.type === 'floor' || station.type === 'wall') return

  const hot = station.container?.cookState === 'cooking' || station.container?.cookState === 'done'
  drawStationFixture(ctx, station.type, box, atTick, hot)

  const { x, y, size } = box

  // A crate IS the ingredient it dispenses, exactly as in the reference: the
  // vegetable sits in the box, big enough to read across the kitchen.
  if (station.type === 'crate' && station.dispenses) {
    const at = TILE_PLACEMENT.crateIngredient
    drawIngredient(ctx, catalog.ingredient(station.dispenses), x + size * at.x, y + size * at.y, size * at.r, atTick)
  }

  // The plate stack's remaining count is a resource the players run out of, so
  // it is drawn as *height* — more plates, taller stack — with a numeral only
  // as a backstop for a stack too tall to count.
  if (station.type === 'plate_stack') drawStackHeight(ctx, station.cleanPlates, box)

  // The return stacks too, in the same shape and a duller glaze, so "how much
  // washing up is waiting" reads at the same glance as "how many plates are
  // left". An empty return is the normal, healthy state, so unlike the stack it
  // is not marked — an empty *stack* is the thing that costs a round.
  if (station.type === 'plate_return') {
    drawStackHeight(ctx, station.dirtyPlates, box, { dirty: true, markEmpty: false })
  }

  // The sink keeps a pile too: plates put down together, washed one by one. An
  // empty sink is the normal state, so, like the return, it is not marked.
  if (station.type === 'sink') {
    drawStackHeight(ctx, station.dirtyPlates ?? 0, box, { dirty: true, markEmpty: false })
  }

  if (station.item) {
    const at = itemPlacement(station.type, station.item)
    drawCarryable(ctx, station.item, x + size * at.x, y + size * at.y, size * at.r, catalog, atTick)
  }

  if (station.container) {
    const at = containerPlacement(station.type)
    drawCarryable(
      ctx,
      { type: 'container', container: station.container },
      x + size * at.x,
      y + size * at.y,
      size * at.r,
      catalog,
      atTick,
    )
  }

  const pan = panDisplay(station, atTick)
  if (pan) {
    if (pan.warning) drawWarningRing(ctx, box, pan.bar?.pulse ?? 0, atTick)
    if (pan.bar) drawBar(ctx, pan.bar, box)
  }

  const hold = holdDisplay(station, atTick, holderColour)

  // The tap/hold split, drawn. A board and a sink say "hold me" before the
  // player has pressed anything; every other station stays silent, and the
  // silence is the other half of the message. Faded once a hold is actually
  // running, because at that point the bar is saying it better.
  if (holdAffordance(station) && !station.onFire) {
    drawHoldGlyph(ctx, x + size * 0.79, y + size * 0.21, size * 0.15, atTick, hold ? 0.3 : 0.9)
  }

  // A burning tile draws its bar after the flames instead — see below.
  if (hold && !station.onFire) drawBar(ctx, hold, box)

  // Time constraint #4. Drawn last so it covers everything: a station on fire
  // cannot be interacted with at all, and the tile has to say so unmissably.
  if (station.onFire) {
    drawFire(ctx, box, atTick, 1 - extinguishProgress(station, atTick))
    if (isSpraying(station)) {
      // Aimed from the chef when we know where they are, and from the lower
      // left when we do not — a cone arriving from somewhere still reads as a
      // jet, and a missing player is not worth dropping the whole effect for.
      drawExtinguisherSpray(
        ctx,
        x + size / 2,
        y + size * 0.62,
        size * 0.5,
        atTick,
        sprayAngle ?? -Math.PI * 0.25,
      )
    }
    // The extinguish bar goes back on top of the flames it is putting out;
    // underneath them it was the one bar in the game you could not read.
    if (hold) drawBar(ctx, hold, box)
  }
}

/**
 * "Hold this key", as a mark.
 *
 * A key cap with a ring winding round it, the ring's gap rotating so the badge
 * is alive without pulsing — a pulse in this kitchen already means "you are
 * running out of time", and this does not mean that. Small and cornered: it is
 * a hint for a player who has not tried yet, not a thing to look at while
 * working.
 */
function drawHoldGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  atTick: number,
  alpha: number,
): void {
  if (r < 4) return
  ctx.save()
  ctx.globalAlpha = alpha

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, TAU)
  ctx.fillStyle = 'rgba(14, 12, 10, 0.72)'
  ctx.fill()

  // The winding ring: three quarters of a circle, its gap walking round.
  const sweep = (atTick * 0.035) % TAU
  ctx.beginPath()
  ctx.arc(cx, cy, r * 0.86, sweep, sweep + TAU * 0.72)
  ctx.lineWidth = Math.max(1, r * 0.18)
  ctx.lineCap = 'round'
  ctx.strokeStyle = COLOURS.ice
  ctx.stroke()

  // The key cap being held down.
  ctx.beginPath()
  roundRect(ctx, cx - r * 0.42, cy - r * 0.34, r * 0.84, r * 0.68, r * 0.18)
  ctx.fillStyle = COLOURS.paper
  ctx.fill()
  ctx.restore()
}

/**
 * The tile's surface.
 *
 * Floors are a sand tile set in grout, walls are a soot face inside a darker
 * edge, and every working station is a walnut bench top with a lit lip along
 * the back and a shadow along the front — so a run of stations looks like one
 * bench and a player can see at a glance where the kitchen's walls are without
 * any of it being labelled. Stations cut into the bench (sink, bin, pass,
 * return) sit a shade darker, so the recess reads before the fixture does.
 */
function drawSurface(ctx: CanvasRenderingContext2D, station: Station, box: TileBox): void {
  const { x, y, size } = box
  const { type } = station

  if (type === 'floor') {
    ctx.fillStyle = COLOURS.floorGrout
    ctx.fillRect(x, y, size, size)
    ctx.fillStyle = COLOURS.floor
    ctx.fillRect(x + size * 0.03, y + size * 0.03, size * 0.94, size * 0.94)
    ctx.strokeStyle = COLOURS.floorLine
    ctx.lineWidth = Math.max(1, size * 0.015)
    ctx.strokeRect(x + size * 0.03, y + size * 0.03, size * 0.94, size * 0.94)
    return
  }

  if (type === 'wall') {
    ctx.fillStyle = COLOURS.wallEdge
    ctx.fillRect(x, y, size, size)
    ctx.fillStyle = COLOURS.wallFace
    ctx.fillRect(x + size * 0.04, y + size * 0.04, size * 0.92, size * 0.92)
    return
  }

  const recessed = RECESSED.has(type)
  const tone = (hex: string): string => (recessed ? shade(hex, -0.25) : hex)
  ctx.fillStyle = COLOURS.counterEdge
  ctx.fillRect(x, y, size, size)
  ctx.fillStyle = tone(COLOURS.counter)
  ctx.fillRect(x + size * 0.02, y + size * 0.02, size * 0.96, size * 0.96)
  // One light source, top-left: a lit lip along the back edge and a shadow
  // along the front. It is what makes the bench read as a solid object.
  ctx.fillStyle = tone(COLOURS.counterLip)
  ctx.fillRect(x + size * 0.02, y + size * 0.02, size * 0.96, size * 0.13)
  ctx.fillStyle = COLOURS.counterShadow
  ctx.fillRect(x + size * 0.02, y + size * 0.88, size * 0.96, size * 0.1)
}

/** The stack, as height. Five rims is the tallest a tile can show. */
function drawStackHeight(
  ctx: CanvasRenderingContext2D,
  plates: number,
  box: TileBox,
  opts: { dirty?: boolean; markEmpty?: boolean } = {},
): void {
  const { dirty = false, markEmpty = true } = opts
  const { x, y, size } = box
  if (plates <= 0) {
    if (!markEmpty) return
    // An empty stack is a real state that costs a round: mark the empty shelf.
    ctx.strokeStyle = 'rgba(255,255,255,0.3)'
    ctx.setLineDash([size * 0.08, size * 0.08])
    ctx.lineWidth = Math.max(1, size * 0.04)
    ctx.beginPath()
    ctx.ellipse(x + size / 2, y + size * 0.66, size * 0.3, size * 0.15, 0, 0, TAU)
    ctx.stroke()
    ctx.setLineDash([])
    return
  }
  const shown = Math.min(5, plates)
  for (let i = 0; i < shown; i += 1) {
    // A drop shadow under each rim, so the plates separate rather than merging
    // into one white disc — the count has to be countable.
    const cy = y + size * (0.7 - i * 0.14)
    ctx.beginPath()
    ctx.ellipse(x + size / 2, cy + size * 0.035, size * 0.3, size * 0.15, 0, 0, TAU)
    ctx.fillStyle = 'rgba(30, 22, 14, 0.45)'
    ctx.fill()
    ctx.beginPath()
    ctx.ellipse(x + size / 2, cy, size * 0.3, size * 0.15, 0, 0, TAU)
    // Dirty plates are the same china, unwashed: greyed and slightly green, so
    // a pile at the return never reads as clean plates ready to serve on.
    ctx.fillStyle = dirty
      ? i === shown - 1
        ? '#c9c2ab'
        : '#b5ae99'
      : i === shown - 1
        ? '#fbf7ef'
        : '#eae4d8'
    ctx.fill()
    ctx.lineWidth = Math.max(1, size * 0.028)
    ctx.strokeStyle = dirty ? '#7c7a63' : '#98a1ab'
    ctx.stroke()
  }
  if (plates > 5) {
    ctx.fillStyle = 'rgba(18,16,14,0.8)'
    roundRect(ctx, x + size * 0.66, y + size * 0.08, size * 0.26, size * 0.2, size * 0.06)
    ctx.fill()
    ctx.fillStyle = COLOURS.text
    ctx.font = font(700, size * 0.17, 'ui-monospace, monospace')
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(plates), x + size * 0.79, y + size * 0.19)
  }
}

/**
 * A burning station. Deliberately the loudest thing on the canvas — it is the
 * one state that stops a player doing anything at all until somebody fetches
 * the extinguisher, so the tile says both "this is on fire" and "the answer is
 * the extinguisher" without a word of text.
 */
function drawFire(
  ctx: CanvasRenderingContext2D,
  box: TileBox,
  atTick: number,
  /** How much fire is left, 1..0, as the extinguish hold accumulates. */
  intensity: number,
): void {
  const { x, y, size } = box
  const alive = Math.max(0, Math.min(1, intensity))
  ctx.save()

  // The tile shakes. Motion is the cue that survives peripheral vision.
  ctx.translate(Math.sin(atTick * 0.9) * size * 0.025, Math.cos(atTick * 1.13) * size * 0.02)

  // A hot core rather than a wash: the tile itself is the brightest thing on
  // the canvas, because a burning station stops the game until it is dealt with.
  const glow = ctx.createRadialGradient(
    x + size / 2,
    y + size * 0.66,
    size * 0.05,
    x + size / 2,
    y + size * 0.6,
    size * 0.78,
  )
  // The whole tile cools as the hold accumulates. This is the channel that is
  // visible from across the kitchen — the flames shrink, but the glow going
  // down is what a partner notices while carrying an onion somewhere else.
  const heat = (0.86 + 0.14 * Math.sin(atTick * 0.3)) * (0.25 + alive * 0.75)
  glow.addColorStop(0, `rgba(255, 226, 138, ${heat})`)
  glow.addColorStop(0.42, `rgba(255, 122, 31, ${heat})`)
  glow.addColorStop(1, 'rgba(120, 26, 6, 0.95)')
  ctx.fillStyle = glow
  ctx.fillRect(x, y, size, size)

  // A hazard border, so "do not touch this" survives greyscale. Only the frame
  // is striped — striping the whole tile made it read as wood grain.
  ctx.save()
  ctx.beginPath()
  ctx.rect(x, y, size, size)
  ctx.rect(x + size * 0.16, y + size * 0.16, size * 0.68, size * 0.68)
  ctx.clip('evenodd')
  ctx.fillStyle = '#1a0f07'
  ctx.fillRect(x, y, size, size)
  ctx.strokeStyle = COLOURS.brass
  ctx.lineWidth = size * 0.075
  const drift = (atTick * 0.5) % (size * 0.34)
  for (let i = -2; i < 8; i += 1) {
    ctx.beginPath()
    ctx.moveTo(x + i * size * 0.34 + drift, y + size + 2)
    ctx.lineTo(x + i * size * 0.34 + drift + size * 0.36, y - 2)
    ctx.stroke()
  }
  ctx.restore()

  // Big enough to be the tile rather than a mark on it.
  drawFlame(ctx, x + size / 2, y + size * 0.86, size * 0.42, atTick, alive)
  drawFlame(ctx, x + size * 0.3, y + size * 0.8, size * 0.24, atTick * 1.4 + 3, alive)
  drawFlame(ctx, x + size * 0.72, y + size * 0.82, size * 0.22, atTick * 1.2 + 7, alive)

  // The instruction, as a picture: an extinguisher on a dark disc, pulsing.
  // "Grab the extinguisher" has to be legible without a word of text, and the
  // disc is what keeps it readable against the flame behind it.
  const pulse = 0.82 + 0.18 * Math.sin(atTick * 0.22)
  const badge = size * 0.19
  ctx.globalAlpha = pulse
  ctx.beginPath()
  ctx.arc(x + size / 2, y + size * 0.27, badge * 1.5, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(16, 10, 6, 0.82)'
  ctx.fill()
  ctx.lineWidth = Math.max(1.5, size * 0.035)
  ctx.strokeStyle = COLOURS.paper
  ctx.stroke()
  drawExtinguisher(ctx, x + size / 2, y + size * 0.27, badge)
  // ...and the ring that says the key is *held*, not tapped. The badge has
  // always said "fetch the extinguisher"; a player who fetched it and tapped
  // was still told nothing, which is the bug this phase was opened for.
  drawHoldGlyph(ctx, x + size * 0.5 + badge * 1.5, y + size * 0.27 + badge * 1.2, badge * 0.62, atTick, 1)
  ctx.globalAlpha = 1

  ctx.restore()
}

/**
 * The escalating burn warning. It rings the whole tile rather than only the
 * bar, because the player who needs to see it is usually across the kitchen
 * doing something else — which is exactly the moment food is lost.
 *
 * Three channels, not one: the ring thickens, the colour ramps amber to red,
 * and the blink speeds up as the square of the elapsed fraction.
 */
function drawWarningRing(
  ctx: CanvasRenderingContext2D,
  box: TileBox,
  pulse: number,
  atTick: number,
): void {
  if (pulse <= 0) return
  const { x, y, size } = box
  const speed = 0.06 + pulse * 0.24
  const blink = 0.5 + 0.5 * Math.sin(atTick * speed)
  const width = Math.max(2, size * (0.05 + pulse * 0.06))

  ctx.save()
  ctx.globalAlpha = Math.min(1, 0.3 + pulse * blink)
  ctx.strokeStyle = pulse > 0.55 ? COLOURS.blood : COLOURS.brass
  ctx.lineWidth = width
  ctx.strokeRect(x + width / 2, y + width / 2, size - width, size - width)

  // Corner ticks: at the top of the ramp the ring grows teeth, so the state is
  // distinguishable from the calm ring without relying on the colour change.
  if (pulse > 0.55) {
    ctx.lineWidth = width * 0.8
    const t = size * 0.22
    for (const [dx, dy, ex, ey] of [
      [0, 0, 1, 1],
      [1, 0, -1, 1],
      [0, 1, 1, -1],
      [1, 1, -1, -1],
    ]) {
      const px = x + dx * size
      const py = y + dy * size
      ctx.beginPath()
      ctx.moveTo(px + ex * t, py)
      ctx.lineTo(px, py)
      ctx.lineTo(px, py + ey * t)
      ctx.stroke()
    }
  }
  ctx.restore()
}

/**
 * The smallest tile that gets a word under its bar.
 *
 * Below this the label is a smudge rather than a sentence — a phone at the
 * narrow layout draws the bar and nothing else, which is the same information
 * this file drew before the label existed.
 */
const MIN_LABELLED_TILE = 56

/**
 * A progress bar, pinned across the bottom of the tile.
 *
 * Taller and darker-backed than phase 7's hairline, with a marked `done` notch:
 * the end of the cooking bar is where the food is ready and everything past it
 * is the burn window.
 *
 * The label is the one place text comes back onto the canvas after phase 8 took
 * it off, and it is a deliberate, narrow exception: `progress.ts` has always
 * computed `Cooking` / `Ready` / `Burning!` / `Burnt — bin it!` / `Paused` and
 * nothing has ever read them. A colour ramp alone asks the player to decode a
 * blend; the word sits under the bar it belongs to and says it outright.
 */
export function drawBar(ctx: CanvasRenderingContext2D, bar: Bar, box: TileBox): void {
  const { size } = box
  // Geometry lives in `progress.ts` with the rest of the checkable decisions —
  // a hold bar is thicker than a cook bar, and that is a number a test can hold
  // us to rather than a literal in a `fillRect`.
  const { x, top, width, height } = barMetrics(box, bar.weight)

  ctx.save()
  roundRect(ctx, x, top, width, height, height / 2)
  ctx.fillStyle = 'rgba(12, 10, 8, 0.72)'
  ctx.fill()

  const filled = Math.max(0, Math.min(1, bar.fraction))
  if (filled > 0) {
    ctx.save()
    roundRect(ctx, x, top, width, height, height / 2)
    ctx.clip()
    ctx.globalAlpha =
      bar.pulse > 0 ? 0.72 + 0.28 * Math.abs(Math.sin(Date.now() * 0.008 * bar.pulse)) : 1
    ctx.fillStyle = bar.colour
    ctx.fillRect(x, top, width * filled, height)
    ctx.globalAlpha = 1
    if (bar.striped) {
      // Diagonal hatching over the filled part only: a paused hold differs
      // from a live one by pattern, not just by a colour a seat might share.
      ctx.beginPath()
      ctx.rect(x, top, width * filled, height)
      ctx.clip()
      ctx.strokeStyle = 'rgba(12, 10, 8, 0.45)'
      ctx.lineWidth = Math.max(1.5, height * 0.28)
      const pitch = Math.max(5, height * 0.9)
      ctx.beginPath()
      for (let sx = x - height; sx < x + width * filled + height; sx += pitch) {
        ctx.moveTo(sx, top + height)
        ctx.lineTo(sx + height, top)
      }
      ctx.stroke()
    }
    ctx.restore()
  }

  // The notch at the far end: the boundary between "cooking" and "burning".
  ctx.strokeStyle = 'rgba(255,255,255,0.65)'
  ctx.lineWidth = Math.max(1, size * 0.018)
  ctx.beginPath()
  ctx.moveTo(x + width - ctx.lineWidth, top - height * 0.25)
  ctx.lineTo(x + width - ctx.lineWidth, top + height * 1.25)
  ctx.stroke()

  // The word for the state, hard against the bar so the two read as one thing.
  // `Bar.label` says "drawn above the bar" and that is where it has to go: the
  // track is already pinned to the bottom edge of the tile, and there is no
  // room below it.
  if (bar.label && size >= MIN_LABELLED_TILE) {
    const fontSize = Math.max(9, Math.round(size * 0.13))
    ctx.font = font(700, fontSize)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'alphabetic'
    const cx = x + width / 2
    const baseline = top - height * 0.42
    // A soft shadow rather than a plate behind the word: the tile underneath is
    // sometimes lit bench and sometimes dark iron, and paper ink on a shadow
    // survives both without boxing in every station.
    ctx.shadowColor = 'rgba(8, 6, 4, 0.9)'
    ctx.shadowBlur = Math.max(2, size * 0.04)
    ctx.fillStyle = COLOURS.paper
    ctx.fillText(bar.label, cx, baseline)
    ctx.shadowBlur = 0
  }
  ctx.restore()
}

/** Still exported for the accessible text layer, which names what it cannot draw. */
export { itemLabel }
